import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";

const flow = [
  { no: "01", title: "收集原始素材", desc: "优先取得未经转发的原图、原视频、原始发布记录，并记录获取路径与时间。" },
  { no: "02", title: "交叉比对", desc: "把不同来源的素材放到同一时间轴上，标出可互相印证的节点与彼此矛盾的位置。" },
  { no: "03", title: "依据分级", desc: "按可追溯性与独立来源数量，把每条依据标注为强、中、弱三档。" },
  { no: "04", title: "给出可信度参考", desc: "综合证据强度输出评分，并说明哪些结论仍需新的依据支持。" },
];

const levels = [
  {
    level: "强",
    color: "border-secondary-300 bg-secondary-50",
    badge: "bg-secondary-100 text-secondary-900 border-secondary-300",
    desc: "来自原始设备或官方渠道，可独立复现、可追溯生成时间，且能被至少一个独立来源印证。",
    example: "例如：不同角度拍摄的原始视频、公开页面存档、可检索的执行标准编号。",
  },
  {
    level: "中",
    color: "border-accent-300 bg-accent-50/70",
    badge: "bg-accent-100 text-accent-950 border-accent-300",
    desc: "来源可说明但无法完全复现，或与其他依据部分吻合、部分存在差异。",
    example: "例如：经过压缩转发的截图、当事人单方声明、环境音被处理过的录音。",
  },
  {
    level: "弱",
    color: "border-background-400 bg-background-100",
    badge: "bg-background-200 text-foreground-700 border-background-400",
    desc: "来源不明、缺少时间信息，或经过多次裁剪，无法与其它依据交叉验证。",
    example: "例如：无出处的聊天截图、缺少编号的检测报告、被裁剪掉上下文的对话。",
  },
];

const bands = [
  { range: "80 - 100", label: "证据较充分", desc: "核心事实已被多个独立来源印证，细节仍有少量待补充。", bar: "bg-secondary-500" },
  { range: "60 - 79", label: "基本可信", desc: "核心事实基本成立，但存在需要进一步确认的关键细节。", bar: "bg-accent-500" },
  { range: "40 - 59", label: "存疑待查", desc: "存在明显矛盾或凭证不足，暂时无法给出方向性判断。", bar: "bg-primary-500" },
  { range: "0 - 39", label: "依据不足", desc: "缺少可核对的原始依据，不建议作为事实引用。", bar: "bg-foreground-500" },
];

const pitfalls = [
  { title: "把裁剪当完整", desc: "截图被裁掉的时间戳与上下文，往往才是判断的关键，不能只看可见部分。" },
  { title: "把重复当独立", desc: "同一素材被多次转发并不等于多个来源，转发链要向上追溯首发记录。" },
  { title: "把情绪当证据", desc: "评论区的一致情绪不能替代可核对的素材，判断始终要回到依据本身。" },
];

export default function MethodPage() {
  usePageMeta({
    title: "真假辨析方法 - 证据分级与可信度评分标准 | 蝌蚪爆料",
    description:
      "蝌蚪爆料的核查方法说明：四步流程、证据强中弱分级标准、可信度评分区间定义，以及截图对比中常见的三类判断误区。",
    keywords: "真假辨析方法,证据分级标准,可信度评分,截图对比方法,蝌蚪爆料可信度",
    canonicalPath: "/method",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "真假辨析方法与证据分级标准",
      url: "https://jiumeijiaju.cn/method",
      description: "四步核查流程、证据分级标准与可信度评分口径说明",
    },
  });

  return (
    <SiteLayout>
      <header className="border-b border-background-300 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 font-label text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">真假辨析</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            真假辨析方法与证据分级
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            公开我们的核查流程与判断口径，让每一起事件的评分都能被读者自行复核，而不是只接受一个数字。
          </p>
        </div>
      </header>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="核查流程"
          title="每起事件都走完这四步"
          description="流程固定、口径统一，避免不同事件使用不同的判断标准。"
        />
        <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-300 bg-background-300 sm:grid-cols-2 lg:grid-cols-4">
          {flow.map((step) => (
            <div key={step.no} className="bg-background-100 p-6">
              <span className="font-heading text-3xl font-bold text-background-400">{step.no}</span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-300 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="证据分级"
            title="三档依据强度对照"
            description="分级只看可追溯性与独立来源数量，与事件本身的热度无关。"
          />
          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {levels.map((item) => (
              <div key={item.level} className={`rounded-lg border p-6 ${item.color}`}>
                <span className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1 font-label text-sm ${item.badge}`}>
                  <i className="ri-signal-tower-line" /> 强度 {item.level}
                </span>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
                <p className="mt-4 border-t border-background-300/70 pt-4 text-xs leading-relaxed text-foreground-500">
                  {item.example}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="评分口径"
          title="可信度评分区间说明"
          description="分数反映的是「当前可用依据的完整度」，不是对当事人的评价。"
        />
        <div className="mt-8 overflow-hidden rounded-lg border border-background-300">
          {bands.map((band) => (
            <div
              key={band.range}
              className="flex flex-col gap-3 border-b border-background-200 bg-background-100 p-5 last:border-b-0 sm:flex-row sm:items-center sm:gap-6"
            >
              <div className="flex items-center gap-3 sm:w-56 sm:shrink-0">
                <span className={`h-8 w-1.5 rounded-full ${band.bar}`} />
                <span className="font-heading text-lg font-bold text-foreground-950">{band.range}</span>
              </div>
              <span className="font-label text-sm font-semibold text-primary-700 sm:w-28 sm:shrink-0">
                {band.label}
              </span>
              <p className="text-sm leading-relaxed text-foreground-600">{band.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-300 bg-foreground-950">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-wide text-accent-300">
              <span className="h-px w-6 bg-accent-400" />
              常见误区
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              判断时最容易踩的三个坑
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {pitfalls.map((p, index) => (
              <div key={p.title} className="rounded-lg border border-background-50/15 bg-background-50/5 p-6">
                <span className="font-heading text-2xl font-bold text-accent-400">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-background-50">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-300">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-background-300 bg-background-100 p-8 md:flex-row md:items-center md:p-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              想看看方法在实际事件里怎么用？
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              追踪列表里的每一起事件，都按同样的流程输出了证据链与时间线。
            </p>
          </div>
          <Link
            to="/topics"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            进入追踪列表 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}